import { create } from 'zustand';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getLiveAccessToken } from '../components/AuthView';
import { refreshAccessTokenShared } from '../services/api';
import { useMaestroStore } from '../store/maestroStore';
import { maybeAutoImportQobuzPlaylists } from '../services/qobuzPlaylistImport';
import { maybeAutoImportQobuzFavorites } from '../services/qobuzFavoritesImport';
import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  recordStreamResolutionFailure,
  fetchStreamUrlWith5xxRetry,
  finalizeStreamUrlErrorResponse,
  resetStreamResolutionSkipCounter,
} from '../transport/streamResolutionSkip';

// ---------------------------------------------------------------------------
// Per-user credential persistence (localStorage)
// ---------------------------------------------------------------------------

function getStoredUserId(): string | null {
  try { return localStorage.getItem('maestro_qobuz_user_id'); } catch { return null; }
}

function storeUserId(id: string) {
  try { localStorage.setItem('maestro_qobuz_user_id', id); } catch {}
}

function clearStoredUserId() {
  try {
    localStorage.removeItem('maestro_qobuz_user_id');
    localStorage.removeItem('maestro_qobuz_user');
  } catch {}
}

function storeUserInfo(user: QobuzUser) {
  try { localStorage.setItem('maestro_qobuz_user', JSON.stringify(user)); } catch {}
}

function getStoredUserInfo(): QobuzUser | null {
  try {
    const s = localStorage.getItem('maestro_qobuz_user');
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

export function getHeaders(): Record<string, string> {
  const authToken = getLiveAccessToken();
  const userId = getStoredUserId();
  return {
    'Content-Type': 'application/json',
    ...(authToken
      ? { Authorization: `Bearer ${authToken}` }
      : userId
        ? { 'X-Maestro-Dev-User': userId, Authorization: `Bearer ${userId}` }
        : {}),
  };
}

async function qobuzFetch(url: string, init?: RequestInit): Promise<Response> {
  const opts = { ...init, credentials: 'omit' as RequestCredentials };
  const resp = await fetch(url, opts);
  if (resp.status !== 401 && resp.status !== 403) return resp;
  try {
    const result = await refreshAccessTokenShared('useQobuz:401-retry');
    const accessToken = result.accessToken;
    if (accessToken) {
      const retryHeaders = {
        ...(init?.headers as Record<string, string> || {}),
        Authorization: `Bearer ${accessToken}`,
      };
      return fetch(url, { ...opts, headers: retryHeaders });
    }
  } catch {}
  return resp;
}

export interface QobuzUser {
  id: number;
  name: string;
  email: string;
  subscription: string;
  qualityCaps: number[];
  bestQuality: number;
}

export interface QobuzAlbum {
  id: string;
  serviceAlbumId: string;
  title: string;
  artistName: string;
  artistId: string;
  coverUrl: string | null;
  coverUrlSmall: string | null;
  releaseDate: string | null;
  trackCount: number;
  duration: number;
  hires: boolean;
  maxBitDepth: number | null;
  maxSampleRate: number | null;
  genre: string | null;
  label: string | null;
  source: string;
  streamable: boolean;
  productType: string | null;
}

export interface QobuzTrack {
  id: string;
  serviceTrackId: string;
  title: string;
  albumId: string;
  albumTitle: string;
  artistName: string;
  artistId: string;
  duration: number;
  trackNumber: number;
  discNumber: number;
  hires: boolean;
  streamable: boolean;
  coverUrl: string | null;
  source: string;
}

export interface QobuzArtist {
  id: string;
  serviceArtistId: string;
  name: string;
  imageUrl: string | null;
  albumCount: number;
  source: string;
}

export interface StreamUrl {
  url: string;
  trackId: string;
  duration: number;
  formatId: number;
  mimeType: string;
  bitDepth: number;
  samplingRate: number;
  blob: string | null;
  source: string;
}

/** Dedup overlapping getStreamUrl calls (same track + effective format) — show-floor / flaky links. */
const _STREAM_URL_CACHE_TTL_MS = 10_000;
const _streamUrlInflight = new Map<string, Promise<StreamUrl | null>>();
const _streamUrlRecent = new Map<string, { data: StreamUrl; at: number }>();

export interface QobuzGenre {
  id: number;
  name: string;
  slug: string;
  color: string | null;
  subgenres?: Array<{ id: number; name: string; slug: string }>;
}

export function shouldShowQobuzConnectPrompt(state: {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  linkResolved?: boolean;
}): boolean {
  if (state.linkResolved === false) return false;
  return !state.connected && !state.connecting && !state.error;
}

interface QobuzState {
  connected: boolean;
  linkResolved: boolean;
  connecting: boolean;
  user: QobuzUser | null;
  error: string | null;

  searchResults: { artists: QobuzArtist[]; albums: QobuzAlbum[]; tracks: QobuzTrack[] } | null;
  searching: boolean;

  artistAlbums: QobuzAlbum[];
  artistAlbumsLoading: boolean;
  artistAlbumsName: string;

  albumTracks: QobuzTrack[];
  albumTracksLoading: boolean;
  currentAlbum: QobuzAlbum | null;
  albumDescription: string | null;

  currentStreamUrl: StreamUrl | null;
  streamLoading: boolean;
  nowPlaying: { track: QobuzTrack; streamUrl: StreamUrl } | null;

  genres: QobuzGenre[];
  genresLoading: boolean;
  genreAlbums: QobuzAlbum[];
  genreAlbumsLoading: boolean;
  currentGenre: { id: number; name: string } | null;

  login: (email: string, password: string) => Promise<boolean>;
  loginOAuth: () => void;
  disconnect: () => void;
  search: (query: string) => Promise<void>;
  getArtistAlbums: (artistName: string) => Promise<void>;
  getAlbumTracks: (albumId: string, album?: QobuzAlbum) => Promise<void>;
  getStreamUrl: (trackId: string, formatId?: number) => Promise<StreamUrl | null>;
  playTrack: (track: QobuzTrack) => Promise<void>;
  pausePlayback: () => void;
  resumePlayback: () => void;
  playNextTrack: () => void;
  playPrevTrack: () => void;
  stopPlayback: () => void;
  clearSearch: () => void;
  loadGenres: () => Promise<void>;
  getGenreAlbums: (genreId: number, genreName: string) => Promise<void>;
}

export const useQobuzStore = create<QobuzState>((set, get) => ({
  connected: false,
  linkResolved: false,
  connecting: false,
  user: null,
  error: null,
  searchResults: null,
  searching: false,
  artistAlbums: [],
  artistAlbumsLoading: false,
  artistAlbumsName: '',
  albumTracks: [],
  albumTracksLoading: false,
  currentAlbum: null,
  albumDescription: null,
  currentStreamUrl: null,
  streamLoading: false,
  nowPlaying: null,
  genres: [],
  genresLoading: false,
  genreAlbums: [],
  genreAlbumsLoading: false,
  currentGenre: null,

  login: async (email: string, password: string) => {
    set({ connecting: true, error: null });
    try {
      const resp = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/dev-login`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (data.success) {
        if (data.userId) storeUserId(data.userId);
        if (data.qobuzUser) storeUserInfo(data.qobuzUser);
        set({ connected: true, linkResolved: true, connecting: false, user: data.qobuzUser });
        const authToken = getLiveAccessToken();
        if (authToken) {
          fetch(`${CLOUD_API_URL}/api/user/streaming-credentials`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({
              service: 'qobuz',
              credentials: { userId: data.userId, email, user: data.qobuzUser },
            }),
          }).catch(() => {});
        }
        // Auto-copy the user's Qobuz playlists and favorites (first time only).
        try { maybeAutoImportQobuzPlaylists(); } catch {}
        try { maybeAutoImportQobuzFavorites(); } catch {}
        return true;
      } else {
        const errorMsg = data.error || data.detail || 'Login failed';
        set({ connecting: false, error: errorMsg });
        return false;
      }
    } catch (err: any) {
      set({ connecting: false, error: err.message });
      return false;
    }
  },

  loginOAuth: () => {
    if (get().connecting) return;
    set({ connecting: true, error: null });

    const authToken = getLiveAccessToken();
    const authUrl = `${CLOUD_API_URL}/api/streaming/qobuz/auth` +
      (authToken ? `?token=${encodeURIComponent(authToken)}` : '');

    fetch(authUrl, { headers: getHeaders() })
      .then(r => r.json())
      .then(async (data: any) => {
        if (!data.redirectUrl) {
          set({ connecting: false, error: 'Could not start Qobuz connection.' });
          return;
        }

        const isTauri = !!(window as any).__TAURI__;
        const isIOS = !!(window as any).__maestroNative;

        if (isTauri) {
          try {
            const core = (window as any).__TAURI__?.core || (window as any).__TAURI__;
            await core.invoke('plugin:shell|open', { path: data.redirectUrl });
          } catch (e) {
            console.error('[Qobuz OAuth] shell.open failed:', e);
            set({ connecting: false, error: 'Could not open browser. Please try again.' });
            return;
          }
          _startOAuthPolling(set, get);
        } else if (isIOS) {
          // iOS native bridge does not have openURL yet — falls through to popup.
          // TODO: Add window.__maestroNative.openURL() to iOS Swift bridge
          _openOAuthPopup(data.redirectUrl, set, get);
        } else {
          _openOAuthPopup(data.redirectUrl, set, get);
        }
      })
      .catch(() => {
        set({ connecting: false, error: 'Could not reach the Maestro server.' });
      });
  },

  disconnect: () => {
    clearStoredUserId();
    // Reset the initial-import flag so reconnecting (possibly as a different
    // Qobuz user) does a fresh import next time.
    import('../services/qobuzPlaylistImport')
      .then(m => m.clearQobuzImportFlag())
      .catch(() => {});
    set({
      connected: false, linkResolved: true, user: null, error: null,
      searchResults: null, artistAlbums: [], albumTracks: [],
      currentAlbum: null, nowPlaying: null, currentStreamUrl: null,
    });
    const authToken = getLiveAccessToken();
    if (authToken) {
      fetch(`${CLOUD_API_URL}/api/streaming/qobuz/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      }).catch(() => {});
      fetch(`${CLOUD_API_URL}/api/user/streaming-credentials/qobuz`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      }).catch(() => {});
    }
  },

  search: async (query: string) => {
    set({ searching: true });
    try {
      const resp = await qobuzFetch(
        `${CLOUD_API_URL}/api/streaming/qobuz/search?q=${encodeURIComponent(query)}&limit=20`,
        { headers: getHeaders() },
      );
      const data = await resp.json();
      set({
        searching: false,
        searchResults: {
          artists: data.artists || [],
          albums: data.albums || [],
          tracks: data.tracks || [],
        },
      });
    } catch {
      set({ searching: false });
    }
  },

  getArtistAlbums: async (artistName: string) => {
    set({ artistAlbumsLoading: true, artistAlbumsName: artistName });
    try {
      const resp = await qobuzFetch(
        `${CLOUD_API_URL}/api/streaming/qobuz/artist-albums?artist=${encodeURIComponent(artistName)}&limit=100`,
        { headers: getHeaders() },
      );
      const data = await resp.json();
      set({ artistAlbumsLoading: false, artistAlbums: data.items || [] });
    } catch {
      set({ artistAlbumsLoading: false });
    }
  },

  getAlbumTracks: async (albumId: string, album?: QobuzAlbum) => {
    set({ albumTracksLoading: true, currentAlbum: album || null, albumDescription: null });
    try {
      const resp = await qobuzFetch(
        `${CLOUD_API_URL}/api/streaming/qobuz/album-tracks?album_id=${albumId}`,
        { headers: getHeaders() },
      );
      const data = await resp.json();
      set({
        albumTracksLoading: false,
        albumTracks: data.tracks || [],
        albumDescription: data.description || data.editorial || data.catchline || null,
      });
    } catch {
      set({ albumTracksLoading: false });
    }
  },

  getStreamUrl: async (trackId: string, formatId = 27) => {
    const effectiveFormatId = formatId;

    const dedupKey = `${trackId}:${effectiveFormatId}`;
    const now = Date.now();
    const recent = _streamUrlRecent.get(dedupKey);
    if (recent && now - recent.at < _STREAM_URL_CACHE_TTL_MS) {
      return recent.data;
    }

    const coalesced = _streamUrlInflight.get(dedupKey);
    if (coalesced) {
      console.log('[Qobuz] Coalescing in-flight getStreamUrl for', dedupKey);
      return coalesced;
    }

    const run = async (): Promise<StreamUrl | null> => {
      console.log(
        '[Qobuz:getStreamUrl] Fetching stream for trackId:',
        trackId,
        'format_id:',
        effectiveFormatId,
      );
      set({ streamLoading: true });

      try {
        const url = `${CLOUD_API_URL}/api/streaming/qobuz/stream-url?track_id=${trackId}&format_id=${effectiveFormatId}`;
        const resp = await fetchStreamUrlWith5xxRetry(() => qobuzFetch(url, { headers: getHeaders() }));
        console.log('[Qobuz:getStreamUrl] Response status:', resp.status);
        if (!resp.ok) {
          set({ streamLoading: false });
          return finalizeStreamUrlErrorResponse(resp, trackId, effectiveFormatId);
        }

        const data = await resp.json();
        if (!data) {
          recordStreamResolutionFailure('unavailable');
          set({ streamLoading: false });
          return null;
        }

        console.log(
          '[Qobuz:getStreamUrl] Stream URL obtained:',
          data?.url ? data.url.slice(0, 80) + '...' : 'NO URL',
          'formatId:', data?.formatId ?? data?.format_id,
          'bitDepth:', data?.bitDepth,
          'sampleRate:', data?.samplingRate,
          'isPreview:', data?.isPreview,
        );
        const actualFmt = data?.formatId ?? data?.format_id ?? effectiveFormatId;
        diagnosticLog(
          'info',
          'protocol',
          `Qobuz getFileUrl: ok — format ${effectiveFormatId}`,
          {
            trackId,
            requestedFormat: effectiveFormatId,
            actualFormat: actualFmt,
            statusCode: resp.status,
            mimeType: resp.headers.get('content-type') || undefined,
          },
        );
        if (!data?.url) {
          recordStreamResolutionFailure('unavailable');
          set({ streamLoading: false });
          return null;
        }

        set({ streamLoading: false, currentStreamUrl: data });
        resetStreamResolutionSkipCounter();
        return data;
      } catch (err) {
        console.error('[Qobuz:getStreamUrl] Exception:', err);
        recordStreamResolutionFailure('unavailable');
        diagnosticLog(
          'error',
          'protocol',
          `Qobuz getFileUrl: failed — format ${effectiveFormatId}`,
          {
            trackId,
            requestedFormat: effectiveFormatId,
            statusCode: undefined,
            mimeType: undefined,
          },
        );
        set({ streamLoading: false });
        return null;
      }
    };

    const promise = run();
    _streamUrlInflight.set(dedupKey, promise);
    try {
      const data = await promise;
      if (data) {
        _streamUrlRecent.set(dedupKey, { data, at: Date.now() });
      }
      return data;
    } finally {
      _streamUrlInflight.delete(dedupKey);
    }
  },

  playTrack: async (track: QobuzTrack) => {
    console.log('[Qobuz:playTrack] Called with:', { serviceTrackId: track.serviceTrackId, id: track.id, title: track.title, source: track.source });
    const svcId = track.serviceTrackId || track.id;
    if (!svcId) {
      console.error('[Qobuz:playTrack] No serviceTrackId or id — cannot play');
      return;
    }
    const { playQobuzTrackOnActiveZone } = await import('../utils/playQobuzTrack');
    await playQobuzTrackOnActiveZone({
      ...track,
      id: svcId,
      serviceTrackId: svcId,
      qobuzTrackId: svcId,
      artist: track.artistName,
      album: track.albumTitle,
      artworkUrl: track.coverUrl,
    });
  },

  playNextTrack: () => {
    const { albumTracks, nowPlaying, playTrack } = get();
    if (!nowPlaying || albumTracks.length === 0) return;
    const idx = albumTracks.findIndex((t) => t.serviceTrackId === nowPlaying.track.serviceTrackId);
    if (idx >= 0 && idx < albumTracks.length - 1) {
      playTrack(albumTracks[idx + 1]);
    }
  },

  playPrevTrack: () => {
    const { albumTracks, nowPlaying, playTrack } = get();
    if (!nowPlaying || albumTracks.length === 0) return;
    const idx = albumTracks.findIndex((t) => t.serviceTrackId === nowPlaying.track.serviceTrackId);
    if (idx > 0) {
      playTrack(albumTracks[idx - 1]);
    }
  },

  pausePlayback: () => {
    import('./qobuzAudioBridge').then(({ qobuzPause }) => qobuzPause());
  },

  resumePlayback: () => {
    import('./qobuzAudioBridge').then(({ qobuzResume }) => qobuzResume());
  },

  stopPlayback: () => {
    import('./qobuzAudioBridge').then(({ qobuzStop }) => qobuzStop());
    set({ nowPlaying: null, currentStreamUrl: null });
  },

  clearSearch: () => {
    set({ searchResults: null, artistAlbums: [], albumTracks: [], currentAlbum: null });
  },

  loadGenres: async () => {
    set({ genresLoading: true });
    try {
      const resp = await fetch(
        `${CLOUD_API_URL}/api/streaming/qobuz/genres`,
        { headers: getHeaders() },
      );
      const data = await resp.json();
      set({ genresLoading: false, genres: data.genres || [] });
    } catch {
      set({ genresLoading: false });
    }
  },

  getGenreAlbums: async (genreId: number, genreName: string) => {
    set({ genreAlbumsLoading: true, currentGenre: { id: genreId, name: genreName } });
    try {
      const resp = await fetch(
        `${CLOUD_API_URL}/api/streaming/qobuz/genre-albums?genre_id=${genreId}&limit=50`,
        { headers: getHeaders() },
      );
      const data = await resp.json();
      set({ genreAlbumsLoading: false, genreAlbums: data.albums || [] });
    } catch {
      set({ genreAlbumsLoading: false });
    }
  },
}));

// ─── OAuth helpers (module-level, outside the store) ──────────────

let _oauthPollInterval: ReturnType<typeof setInterval> | null = null;
let _oauthPollTimeout: ReturnType<typeof setTimeout> | null = null;

function _clearOAuthPolling() {
  if (_oauthPollInterval) { clearInterval(_oauthPollInterval); _oauthPollInterval = null; }
  if (_oauthPollTimeout) { clearTimeout(_oauthPollTimeout); _oauthPollTimeout = null; }
}

export function cancelOAuthLogin() {
  _clearOAuthPolling();
  useQobuzStore.setState({ connecting: false, error: null });
}

function _startOAuthPolling(
  set: (partial: Partial<QobuzState>) => void,
  get: () => QobuzState,
) {
  _clearOAuthPolling();

  _oauthPollTimeout = setTimeout(() => {
    _clearOAuthPolling();
    if (get().connecting) {
      set({
        connecting: false,
        error: 'Connection timed out. If you completed sign-in with Qobuz, try refreshing. Otherwise, please try again.',
      });
    }
  }, 120_000);

  _oauthPollInterval = setInterval(async () => {
    const authToken = getLiveAccessToken();
    if (!authToken) return;
    try {
      const resp = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/status`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (resp.status === 401) {
        _clearOAuthPolling();
        set({
          connecting: false,
          error: 'Your Maestro session expired. Please sign in to Maestro again, then retry the Qobuz connection.',
        });
        return;
      }
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.connected === true) {
        _clearOAuthPolling();
        const user = {
          name: data.displayName || null,
          subscription: data.subscriptionTier || null,
          qualityCaps: data.qualityCaps || [],
        };
        set({ connected: true, linkResolved: true, connecting: false, user, error: null });
        restoreStreamingCredentials().catch(() => {});
      }
    } catch {}
  }, 2000);
}

function _openOAuthPopup(
  redirectUrl: string,
  set: (partial: Partial<QobuzState>) => void,
  get: () => QobuzState,
) {
  const w = 500, h = 700;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(
    redirectUrl,
    'qobuz_oauth',
    `width=${w},height=${h},left=${left},top=${top},popup=1`
  );

  const onMessage = (ev: MessageEvent) => {
    if (ev.data?.type !== 'maestro-oauth-result') return;
    window.removeEventListener('message', onMessage);
    clearInterval(pollClosed);
    _clearOAuthPolling();

    if (ev.data.success) {
      set({ connected: true, linkResolved: true, connecting: false, error: null });
      restoreStreamingCredentials().catch(() => {});
    } else {
      set({ connecting: false, error: ev.data.detail || 'Qobuz connection failed.' });
    }
  };
  window.addEventListener('message', onMessage);

  const pollClosed = setInterval(() => {
    if (popup && popup.closed) {
      clearInterval(pollClosed);
      window.removeEventListener('message', onMessage);
    }
  }, 500);

  _startOAuthPolling(set, get);
}

export async function restoreStreamingCredentials(): Promise<void> {
  const authToken = getLiveAccessToken();
  if (!authToken) {
    useQobuzStore.setState({ linkResolved: true });
    return;
  }

  try {
    const resp = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/status`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!resp.ok) {
      useQobuzStore.setState({ linkResolved: true });
      return;
    }
    const data = await resp.json();
    if (data.connected === true) {
      const cached = getStoredUserInfo();
      const user = {
        id: cached?.id ?? (Number(data.displayName) || 0),
        name: cached?.name || data.displayName || null,
        email: cached?.email || '',
        subscription: data.subscriptionTier || cached?.subscription || null,
        qualityCaps: data.qualityCaps || cached?.qualityCaps || [],
        bestQuality: Array.isArray(data.qualityCaps) && data.qualityCaps.length
          ? Math.max(...data.qualityCaps)
          : (cached?.bestQuality || 6),
      };
      if (data.displayName) storeUserId(String(data.displayName));
      storeUserInfo(user);
      useQobuzStore.setState({ connected: true, linkResolved: true, user, error: null });
    } else {
      clearStoredUserId();
      useQobuzStore.setState({ connected: false, linkResolved: true, user: null });
    }
  } catch {
    useQobuzStore.setState({ linkResolved: true });
  }

  try {
    if (useQobuzStore.getState().connected) {
      maybeAutoImportQobuzPlaylists();
      maybeAutoImportQobuzFavorites();
    }
  } catch {}
}

/**
 * Clear the local "initial-import-done" flag on Qobuz disconnect, so the next
 * successful login for THIS user (or a different Qobuz account) gets a fresh import.
 */
export function clearQobuzImportFlagOnDisconnect(): void {
  try {
    import('../services/qobuzPlaylistImport')
      .then(m => m.clearQobuzImportFlag())
      .catch(() => {});
    import('../services/qobuzFavoritesImport')
      .then(m => m.clearQobuzFavoritesImportFlag())
      .catch(() => {});
  } catch {}
}

